import { useState } from 'react'
import { Layout, Menu, Avatar, Dropdown, Badge, Space } from 'antd'
import {
  DashboardOutlined,
  EditOutlined,
  SearchOutlined,
  SettingOutlined,
  TeamOutlined,
  TagsOutlined,
  FileTextOutlined,
  MonitorOutlined,
  DatabaseOutlined,
  ApiOutlined,
  RobotOutlined,
  BellOutlined,
  UserOutlined,
  LogoutOutlined,
  DownOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  CloudUploadOutlined,
  NodeIndexOutlined,
} from '@ant-design/icons'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../stores/auth'

const { Header, Sider, Content } = Layout

const MainLayout = () => {
  const [collapsed, setCollapsed] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '个人中心',
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: '账号设置',
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      danger: true,
      onClick: handleLogout,
    },
  ]

  // 根据角色生成菜单 - 重新设计
  const getMenuItems = () => {
    const role = user?.role

    // 标注员菜单
    if (role === 'annotator') {
      return [
        {
          key: '/dashboard',
          icon: <DashboardOutlined />,
          label: '工作台',
        },
        {
          key: '/upload-clean',
          icon: <CloudUploadOutlined />,
          label: '数据上传清洗',
        },
        {
          key: '/annotation',
          icon: <EditOutlined />,
          label: '标注工作台',
        },
        {
          key: '/case-search',
          icon: <SearchOutlined />,
          label: '案例查询',
        },
        {
          key: '/knowledge-graph',
          icon: <NodeIndexOutlined />,
          label: '智能知识图谱',
        },
      ]
    }

    // 审核员菜单
    if (role === 'reviewer') {
      return [
        {
          key: '/dashboard',
          icon: <DashboardOutlined />,
          label: '工作台',
        },
        {
          key: '/upload-clean',
          icon: <CloudUploadOutlined />,
          label: '数据上传清洗',
        },
        {
          key: '/annotation',
          icon: <EditOutlined />,
          label: '标注工作台',
        },
        {
          key: '/case-search',
          icon: <SearchOutlined />,
          label: '案例查询',
        },
        {
          key: '/knowledge-graph',
          icon: <NodeIndexOutlined />,
          label: '智能知识图谱',
        },
      ]
    }

    // 信息查询员菜单
    if (role === 'query_user') {
      return [
        {
          key: '/dashboard',
          icon: <DashboardOutlined />,
          label: '工作台',
        },
        {
          key: '/case-search',
          icon: <SearchOutlined />,
          label: '案例查询',
        },
        {
          key: '/knowledge-graph',
          icon: <NodeIndexOutlined />,
          label: '智能知识图谱',
        },
      ]
    }

    // 管理员菜单 - 全部功能
    if (role === 'admin') {
      return [
        {
          key: '/dashboard',
          icon: <DashboardOutlined />,
          label: '工作台',
        },
        {
          key: '/upload-clean',
          icon: <CloudUploadOutlined />,
          label: '数据上传清洗',
        },
        {
          key: '/annotation',
          icon: <EditOutlined />,
          label: '标注工作台',
        },
        {
          key: '/case-search',
          icon: <SearchOutlined />,
          label: '案例查询',
        },
        {
          key: '/knowledge-graph',
          icon: <NodeIndexOutlined />,
          label: '智能知识图谱',
        },
        {
          key: 'advanced',
          icon: <DatabaseOutlined />,
          label: '高级功能',
          children: [
            {
              key: '/admin/dataset',
              icon: <DatabaseOutlined />,
              label: '数据集下载',
            },
            {
              key: '/admin/api',
              icon: <ApiOutlined />,
              label: '数据接口',
            },
            {
              key: '/admin/model',
              icon: <RobotOutlined />,
              label: '模型微调',
            },
          ],
        },
        {
          key: 'admin',
          icon: <SettingOutlined />,
          label: '系统管理',
          children: [
            {
              key: '/admin/users',
              icon: <TeamOutlined />,
              label: '用户管理',
            },
            {
              key: '/admin/tags',
              icon: <TagsOutlined />,
              label: '标签配置',
            },
            {
              key: '/admin/settings',
              icon: <SettingOutlined />,
              label: '系统设置',
            },
            {
              key: '/admin/logs',
              icon: <FileTextOutlined />,
              label: '操作日志',
            },
            {
              key: '/admin/monitor',
              icon: <MonitorOutlined />,
              label: '系统监控',
            },
          ],
        },
      ]
    }

    return []
  }

  const menuItems = getMenuItems()

  // 获取当前选中的菜单项
  const getSelectedKeys = () => {
    const pathname = location.pathname
    if (pathname.startsWith('/admin/')) {
      return [pathname]
    }
    if (pathname.startsWith('/annotation')) {
      return ['/annotation']
    }
    if (pathname.startsWith('/review')) {
      return ['/review']
    }
    return [pathname]
  }

  // 获取展开的菜单项
  const getOpenKeys = () => {
    const pathname = location.pathname
    if (pathname.startsWith('/admin/')) {
      if (['/admin/users', '/admin/tags', '/admin/settings', '/admin/logs', '/admin/monitor'].includes(pathname)) {
        return ['admin']
      }
      if (['/admin/dataset', '/admin/api', '/admin/model'].includes(pathname)) {
        return ['advanced']
      }
    }
    return []
  }



  // 判断是否在 admin 页面
  const isAdminPage = location.pathname.startsWith('/admin')

  return (
    <Layout className="min-h-screen">
      <Header className="flex items-center justify-between px-6">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3">
            <div
              className={`transition-all duration-300 ${isAdminPage ? 'h-12' : 'h-8'}`}
              style={{
                transform: isAdminPage ? 'scale(1.5)' : 'scale(1)',
                transformOrigin: 'left center',
              }}
            >
              <img
                src="/logo.png"
                alt="Logo"
                className="h-full w-auto"
                style={{
                  filter: 'invert(1) brightness(2)',
                }}
              />
            </div>
            <span className="text-white text-xl font-bold">招投标质疑数据标注平台</span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Badge count={5}>
            <BellOutlined className="text-white text-lg cursor-pointer" />
          </Badge>
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Space className="cursor-pointer text-white">
              <Avatar icon={<UserOutlined />} />
              <span className="hidden sm:inline">{user?.username}</span>
              <DownOutlined className="text-xs" />
            </Space>
          </Dropdown>
        </div>
      </Header>
      <Layout>
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          width={220}
          className="bg-white"
        >
          <div className="p-4">
            <Menu
              mode="inline"
              selectedKeys={getSelectedKeys()}
              defaultOpenKeys={getOpenKeys()}
              items={menuItems}
              onClick={({ key }) => {
                console.log('Menu clicked:', key)
                navigate(key)
              }}
              className="border-r-0"
            />
          </div>
          <div
            className="absolute bottom-0 left-0 right-0 p-4 text-center cursor-pointer hover:bg-gray-100"
            onClick={() => setCollapsed(!collapsed)}
          >
            {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          </div>
        </Sider>
        <Content className="p-6 bg-bg-primary overflow-auto">
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  )
}

export default MainLayout
